/**
 * TemplatePreview
 *
 * Mockup estilo WhatsApp Business mostrando como o template renderizado
 * vai aparecer para o destinatário, com variáveis substituídas pelos
 * sample values informados.
 */

import React from 'react';
import {
  IconPhoto,
  IconVideo,
  IconFile,
  IconMapPin,
  IconExternalLink,
  IconPhone,
  IconCornerDownLeft,
} from '@tabler/icons-react';

/**
 * Substitui {{1}}, {{2}} etc. pelos values fornecidos.
 * `values` pode ser array ['João', 'Premium'] ou objeto {1: 'João', 2: 'Premium'}.
 */
function applyVars(text, values = []) {
  if (!text) return '';
  return text.replace(/\{\{(\d+)\}\}/g, (_, idx) => {
    const i = Number(idx) - 1;
    if (Array.isArray(values)) return values[i] != null ? String(values[i]) : `{{${idx}}}`;
    return values[idx] != null ? String(values[idx]) : `{{${idx}}}`;
  });
}

const HeaderContent = ({ component }) => {
  if (!component) return null;

  const fmt = component.format || 'TEXT';
  const headerExample = component.example?.header_text?.[0] || component.example?.header_handle?.[0];

  if (fmt === 'TEXT') {
    const sample = component.example?.header_text || [];
    const text = applyVars(component.text, sample);
    return (
      <div className="tmpl-prev__header tmpl-prev__header--text">
        <strong>{text}</strong>
      </div>
    );
  }

  const placeholderMap = {
    IMAGE: { Icon: IconPhoto, label: 'Imagem' },
    VIDEO: { Icon: IconVideo, label: 'Vídeo' },
    DOCUMENT: { Icon: IconFile, label: 'Documento' },
    LOCATION: { Icon: IconMapPin, label: 'Localização' },
  };
  const ph = placeholderMap[fmt];
  if (!ph) return null;
  const Icon = ph.Icon;

  return (
    <div className="tmpl-prev__header tmpl-prev__header--media">
      <Icon size={18} />
      <span>{ph.label} {headerExample ? '(exemplo)' : ''}</span>
    </div>
  );
};

const ButtonItem = ({ btn }) => {
  if (!btn) return null;
  const iconMap = {
    URL: IconExternalLink,
    PHONE_NUMBER: IconPhone,
    QUICK_REPLY: IconCornerDownLeft,
  };
  const Icon = iconMap[btn.type] || IconCornerDownLeft;
  return (
    <button type="button" className="tmpl-prev__button" disabled>
      <Icon size={14} />
      <span>{btn.text || '(sem texto)'}</span>
    </button>
  );
};

const TemplatePreview = ({ template, sampleValues = {}, dense = false }) => {
  if (!template) return null;
  const components = template.components || [];

  const header = components.find((c) => c.type === 'HEADER');
  const body = components.find((c) => c.type === 'BODY');
  const footer = components.find((c) => c.type === 'FOOTER');
  const buttonsComp = components.find((c) => c.type === 'BUTTONS');

  const bodyText = body
    ? applyVars(body.text, sampleValues.body || body.example?.body_text?.[0] || [])
    : '';

  return (
    <div className={`tmpl-prev ${dense ? 'tmpl-prev--dense' : ''}`}>
      <div className="tmpl-prev__bubble">
        {header && <HeaderContent component={header} />}
        {bodyText && <div className="tmpl-prev__body">{bodyText}</div>}
        {footer?.text && <div className="tmpl-prev__footer">{footer.text}</div>}
        <div className="tmpl-prev__time">12:34</div>
      </div>
      {buttonsComp?.buttons?.length > 0 && (
        <div className="tmpl-prev__buttons">
          {buttonsComp.buttons.map((btn, i) => (
            <ButtonItem key={i} btn={btn} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatePreview;
